import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextInput } from "../../reusables/FormElements";
import UsersTable from "../../sub_components/UsersTable";

import { IOrganizationData, IOrganizationFormProps, IUserData, OrganizationField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";
import { TOrganizationStatus } from "../../../interfaces/types";

import { closeModal, openModal } from "../../../store/slices/modal";

import useSaveOrganization from "../../../hooks/organizations/useSaveOrganization";
import useGetOrganizationUsers from "../../../hooks/users/useGetOrganizationUsers";
import useInviteUsers from "../../../hooks/users/useInviteUsers";

import { OPOrganizationStatuses } from "../../../data/options";
import { Assessments } from "../../sub_components/Assessments";

const OrganizationForm = (props: IOrganizationFormProps) => {
    const { organizationData, selected, setSelected, setExpanded } = props; // eslint-disable-line

    const dispatch = useDispatch();

    const requiredFields: OrganizationField[] = ["name", "status", "user_name", "user_email"];
    const selectFields: OrganizationField[] = ["status"];

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<IOrganizationData>({
        id: null,
        name: { value: "", error: "" },
        status: { value: { label: "", value: "" }, error: "" },
        user_name: { value: "", error: "" },
        user_email: { value: "", error: "" },
        user_phone: { value: "", error: "" },
    });
    const [users, setUsers] = useState<IUserData[]>([]);
    const [originalUsers, setOriginalUsers] = useState<IUserData[]>([]);

    const { mutate: saveOrganization } = useSaveOrganization();
    const { data: usersRes, isLoading } = useGetOrganizationUsers(formData.id);
    const { mutate: inviteUsers } = useInviteUsers();

    useEffect(() => {
        if (organizationData) {
            const status = OPOrganizationStatuses.find(s => s.value === organizationData.status);

            setFormData({
                id: organizationData.id > 0 ? organizationData.id : null,
                name: { value: organizationData.name, error: "" },
                status: { value: { value: status?.value ?? "", label: status?.label ?? "" }, error: "" },
                user_name: { value: organizationData.id > 0 ? "FILLED" : "", error: "" },
                user_email: { value: organizationData.id > 0 ? "FILLED" : "", error: "" },
                user_phone: { value: "", error: "" },
            });
        } else {
            setFormData({
                id: null,
                name: { value: "", error: "" },
                status: { value: { label: "", value: "" }, error: "" },
                user_name: { value: "", error: "" },
                user_email: { value: "", error: "" },
                user_phone: { value: "", error: "" },
            });
        };

        // eslint-disable-next-line
    }, [organizationData]);

    const onChangeText = (name: OrganizationField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: OrganizationField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as unknown as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.name.value === (organizationData?.name ?? '') &&
            formData.status.value.value === (organizationData?.status ?? '');

        if (JSON.stringify(users) !== JSON.stringify(originalUsers)) {
            return users.some(user => !user.name?.trim()?.length || !user.email?.trim()?.length);
        };

        return emptyFields || isDataSame || isProcessing;
    };

    const isOkToInvite = (from: string): boolean => {
        // sequence matters here

        if (!isDisabled()) return false;

        if (organizationData?.status === 'setup' || organizationData?.status === 'disabled') return false;

        if (JSON.stringify(users) !== JSON.stringify(originalUsers)) {
            if (users.some(user => !user.id)) return false;
        };

        if (users.every(user => user.verified)) return false;

        return true;
    };

    const onSave = () => {
        if (isDisabled() || !selected) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as OrganizationField].error = "";
            });
            return newData;
        });

        setIsProcessing(true);

        saveOrganization({
            payload: {
                id: formData.id,
                name: formData.name.value,
                status: formData.status.value.value as TOrganizationStatus,
                user_name: formData.id ? null : formData.user_name.value,
                user_email: formData.id ? null : formData.user_email.value,
                user_phone: formData.id ? null : formData.user_phone.value?.trim()?.length ? formData.user_phone.value.replace(/[^0-9]/g, '') : null,
            },
            usersPayload: users,
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete organization?",
                content: "This will also delete all associated employers and users.",
                actionTxt: "Delete",
                action: () => {
                    // setIsProcessing(true);

                    // deleteOrganization({
                    //     id: formData.id as number,
                    //     setIsProcessing,
                    //     setSelected,
                    //     setExpanded,
                    // });

                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    const onInviteUsers = (user: IUserData | null) => {
        if (!formData.id || (user && !user.id)) return;

        let modalTitle = "Confirm invite all users?";
        let modalContent = "This will send invitation emails to all unverified users associated with this organization.";
        let payload = users.filter(user => user.id !== null).map(user => user.id as number);
        if (user?.id) {
            if (user.verified) return;

            modalTitle = "Confirm invite user?";
            modalContent = `This will send an invitation email to ${user.name} (${user.email}).`;
            payload = [user.id];
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: modalTitle,
                content: modalContent,
                actionTxt: "Invite",
                action: () => {
                    setIsProcessing(true);

                    inviteUsers({
                        payload,
                        setIsProcessing,
                    });
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    return (
        <div className="immployer_admin__common_form">
            <div className="immployer_admin__common_form__inputs">
                <TextInput
                    name="name"
                    type="text"
                    max_length={100}
                    label="Organization Name"
                    required={true}
                    errorMsg={formData.name.error}
                    placeholder="Enter organization name"
                    onChange={(value: string) => onChangeText("name", value)}
                    value={formData.name.value}
                />
                {!formData.id
                    ? <>
                        <TextInput
                            name="user_name"
                            type="text"
                            max_length={100}
                            label="User Name"
                            required={true}
                            errorMsg={formData.user_name.error}
                            placeholder="Enter user name"
                            onChange={(value: string) => onChangeText("user_name", value)}
                            value={formData.user_name.value}
                        />
                        <TextInput
                            name="user_email"
                            type="email"
                            max_length={100}
                            label="User Email"
                            required={true}
                            errorMsg={formData.user_email.error}
                            placeholder="Enter user email"
                            onChange={(value: string) => onChangeText("user_email", value)}
                            value={formData.user_email.value}
                        />
                        <TextInput
                            name="user_phone"
                            type="text"
                            max_length={12}
                            placeholder="650-000-0000"
                            pattern_mask="phone_number"
                            label="User Phone"
                            errorMsg={formData.user_phone.error}
                            onChange={(value: string) => onChangeText("user_phone", value)}
                            value={formData.user_phone.value}
                        />
                    </>
                    : <>
                        <SelectDropdown
                            name="status"
                            label="Organization Status"
                            required={true}
                            placeholder="-- select status --"
                            value={formData.status.value}
                            onChange={(option: ISelectDropdownOption) => onChangeSelect("status", option)}
                            errorMsg={formData.status.error}
                            options={OPOrganizationStatuses}
                        />
                        <br />
                        <h4 className="color_dark">Users:</h4>
                        <UsersTable
                            organizationId={formData.id}
                            usersRes={usersRes ?? []}
                            users={users}
                            setUsers={setUsers}
                            originalUsers={originalUsers}
                            setOriginalUsers={setOriginalUsers}
                            isLoading={isLoading}
                            onInviteUsers={onInviteUsers}
                            organizationStatus={organizationData?.status}
                            isOkToInvite={isOkToInvite}
                        />
                        <br />
                        <h4 className="color_dark">Assessments:</h4>
                        <Assessments organizationId={formData.id} />
                        <br /><br />
                    </>
                }
            </div>
            <div className="immployer_admin__common_form__actions">
                {!formData.id &&
                    <button
                        className={`immployer_admin__btn immployer_admin__btn__secondary ${isProcessing ? "immployer_admin__btn__secondary__disabled" : ""}`}
                        onClick={() => isProcessing ? null : onDelete()}
                    >
                        {!formData.id ? 'Discard' : 'Delete'}
                    </button>
                }
                {formData.id &&
                    <button
                        className={`immployer_admin__btn immployer_admin__btn__secondary ${isOkToInvite('all') ? "" : "immployer_admin__btn__secondary__disabled"}`}
                        onClick={() => isOkToInvite('all') ? onInviteUsers(null) : null}
                    >
                        {
                            isProcessing
                                ? <i className='immployer_admin__btn_loader' />
                                : 'Invite All Users'
                        }
                    </button>
                }
                <button
                    className={`immployer_admin__btn immployer_admin__btn__primary ${isDisabled() ? "immployer_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immployer_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default OrganizationForm;